import Logger from '../../../utils/Logger';

/**
 * BaseDatafeed - Abstract base class for TradingView datafeed implementations
 * 
 * This class defines the common interface and shared logic for all market types
 * (Spot, Futures, Options, etc.). Subclasses must implement market-specific methods.
 */

class BaseDatafeed {
  constructor(config) {
    this.config = config;
    this.subscribers = {};
    this.socket = null;
    this.supportedResolutions = ['1', '3', '5', '15', '30', '60', '120', '240', '1D', '1W', '1M'];
    this.activeSymbol = null; // Track currently active symbol (e.g., "INR/BTC")
    this.activeChannel = null; // Track currently active channel (e.g., "btcinr@kline_4h")
  }

  /**
   * Abstract Methods - Must be implemented by subclasses
   */

  /**
   * Initialize socket connection (Socket.io vs WebSocket)
   */
  initializeSocket() {
    throw new Error('initializeSocket() must be implemented by subclass');
  }

  /**
   * Format symbol for API requests
   * @param {string} baseCoin - Base currency (e.g., "INR")
   * @param {string} cryptoCoin - Crypto currency (e.g., "BTC")
   * @returns {string} - Formatted symbol
   */
  formatSymbolForAPI(baseCoin, cryptoCoin) {
    throw new Error('formatSymbolForAPI() must be implemented by subclass');
  }

  /**
   * Format symbol for socket subscription
   * @param {string} baseCoin - Base currency
   * @param {string} cryptoCoin - Crypto currency
   * @param {string} resolution - Chart resolution
   * @returns {string} - Socket channel/stream name
   */
  formatSymbolForSocket(baseCoin, cryptoCoin, resolution) {
    throw new Error('formatSymbolForSocket() must be implemented by subclass');
  }

  /**
   * Fetch historical bars from API
   * @param {Object} symbolInfo - TradingView symbol info
   * @param {string} resolution - Chart resolution
   * @param {Object} periodParams - from, to, firstDataRequest
   * @returns {Promise<Array>} - Array of bars
   */
  async fetchHistoricalBars(symbolInfo, resolution, periodParams) {
    throw new Error('fetchHistoricalBars() must be implemented by subclass');
  }

  /**
   * Parse real-time update from socket message
   * @param {Object} data - Raw socket message
   * @returns {Object|null} - Parsed bar data or null
   */
  parseSocketMessage(data) {
    throw new Error('parseSocketMessage() must be implemented by subclass');
  }

  /**
   * Subscribe to real-time updates via socket
   * @param {string} channelString - Subscription channel
   */
  subscribeToSocket(channelString) {
    throw new Error('subscribeToSocket() must be implemented by subclass');
  }

  /**
   * Unsubscribe from real-time updates
   * @param {string} channelString - Subscription channel
   */
  unsubscribeFromSocket(channelString) {
    throw new Error('unsubscribeFromSocket() must be implemented by subclass');
  }

  /**
   * Shared TradingView API Methods (Common across all markets)
   */

  onReady(callback) {
    setTimeout(() => {
      callback({
        supported_resolutions: this.supportedResolutions,
        supports_marks: false,
        supports_timescale_marks: false,
        supports_time: true,
        supports_search: false,
      });
    }, 0);
  }

  searchSymbols(userInput, exchange, symbolType, onResultReadyCallback) {
    // Override in subclass if search is needed
    onResultReadyCallback([]);
  }

  resolveSymbol(symbolName, onSymbolResolvedCallback, onResolveErrorCallback) {
    const [baseCoin, cryptoCoin] = symbolName.split('/');
    
    const symbolInfo = {
      ticker: symbolName,
      name: symbolName,
      description: `${cryptoCoin}/${baseCoin}`,
      type: 'crypto',
      session: '24x7',
      timezone: this.config.timezone || 'Asia/Kolkata',
      exchange: this.config.exchange || '',
      minmov: 1,
      pricescale: Math.pow(10, this.config.priceScale ?? 2),
      has_intraday: true,
      has_daily: true,
      has_weekly_and_monthly: true,
      supported_resolutions: this.supportedResolutions,
      volume_precision: 8,
      data_status: 'streaming',
    };

    setTimeout(() => onSymbolResolvedCallback(symbolInfo), 0);
  }

  async getBars(symbolInfo, resolution, periodParams, onHistoryCallback, onErrorCallback) {
    try {
      const { from, to, firstDataRequest } = periodParams;
      
      Logger.info(`[getBars] Request for ${symbolInfo.ticker} [${resolution}]: from=${new Date(from * 1000).toISOString()}, to=${new Date(to * 1000).toISOString()}, firstDataRequest=${firstDataRequest}`);
      
      const bars = await this.fetchHistoricalBars(symbolInfo, resolution, periodParams);
      
      if (bars.length === 0) {
        // Check if we're requesting future data (beyond current time)
        const now = Date.now() / 1000; // Current time in seconds
        const isFutureRequest = from > now;
        
        if (isFutureRequest) {
          Logger.warn('[getBars] Requested range is in the future - signaling noData but allowing pagination');
          onHistoryCallback([], { noData: false }); // Allow continuing to historical data
        } else {
          Logger.info('[getBars] API returned empty array - no more historical data available');
          onHistoryCallback([], { noData: true }); // Stop pagination
        }
        return;
      }

      Logger.info(`[getBars] Received ${bars.length} bars: ${new Date(bars[0].time).toISOString()} to ${new Date(bars[bars.length - 1].time).toISOString()}`);
      Logger.info(`[getBars] Requested range: ${new Date(from * 1000).toISOString()} to ${new Date(to * 1000).toISOString()}`);

      // Only filter if bars extend beyond the requested 'to' time
      // Always include bars that are before 'to', even if they're before 'from'
      const filteredBars = bars.filter(bar => {
        const barTimeSeconds = Math.floor(bar.time / 1000);
        return barTimeSeconds < to; // Only filter out bars that are in the future
      });

      if (filteredBars.length === 0) {
        Logger.warn('[getBars] All bars are after requested range - continuing pagination');
        onHistoryCallback([], { noData: false });
        return;
      }

      Logger.info(`[getBars] Returning ${filteredBars.length} bars after filtering`);
      
      // Always signal that more data might be available (noData: false)
      // TradingView will stop requesting when we return empty array
      onHistoryCallback(filteredBars, { noData: false });
    } catch (error) {
      Logger.error('[getBars] Error:', error);
      onErrorCallback(error);
    }
  }

  subscribeBars(symbolInfo, resolution, onRealtimeCallback, subscriberUID, onResetCacheNeededCallback) {
    const [baseCoin, cryptoCoin] = symbolInfo.ticker.split('/');
    const channelString = this.formatSymbolForSocket(baseCoin, cryptoCoin, resolution);
    const newSymbol = symbolInfo.ticker;

    console.info(`📡 [subscribeBars] Request for symbol: ${newSymbol}, resolution: ${resolution}, channel: ${channelString}`);

    // Detect symbol change (e.g., switching from BTC to TRX)
    const symbolChanged = this.activeSymbol && this.activeSymbol !== newSymbol;
    
    if (symbolChanged) {
      Logger.warn(`[subscribeBars] Symbol changed from ${this.activeSymbol} to ${newSymbol}`);
      
      // Unsubscribe from old symbol's channel
      if (this.activeChannel) {
        Logger.info(`[subscribeBars] Unsubscribing from old channel: ${this.activeChannel}`);
        this.unsubscribeFromSocket(this.activeChannel);
      }
      
      // Clear chart cache to remove old symbol's candles
      Logger.info('[subscribeBars] Triggering chart cache reset');
      if (onResetCacheNeededCallback) {
        onResetCacheNeededCallback();
      }
      
      // Clear all old subscribers
      this.subscribers = {};
    }
    
    // Update active symbol and channel
    this.activeSymbol = newSymbol;
    this.activeChannel = channelString;

    // Store subscriber
    this.subscribers[subscriberUID] = {
      symbolInfo,
      resolution,
      onRealtimeCallback,
      channelString,
    };

    console.info(`📋 [subscribeBars] Stored subscriber with UID: ${subscriberUID}, Total subscribers: ${Object.keys(this.subscribers).length}`);

    // Initialize socket if not connected
    if (!this.socket) {
      console.info('🔌 [subscribeBars] Initializing socket...');
      this.initializeSocket();
    } else {
      console.info('✅ [subscribeBars] Socket already initialized, state:', this.socket.readyState);
    }

    // Subscribe to new channel
    this.subscribeToSocket(channelString);

    console.info(`📡 Subscribed to ${channelString} [UID: ${subscriberUID}] (Active: ${this.activeSymbol})`);
  }

  unsubscribeBars(subscriberUID) {
    const subscriber = this.subscribers[subscriberUID];
    
    if (subscriber) {
      this.unsubscribeFromSocket(subscriber.channelString);
      delete this.subscribers[subscriberUID];
      console.log(`🔌 Unsubscribed from ${subscriber.channelString}`);
    }
  }

  /**
   * Helper method to handle socket messages (called by subclasses)
   */
  handleSocketMessage(data, channelString) {
    // Layer 1: Check if this is from an active channel
    if (this.activeChannel && channelString !== this.activeChannel) {
      Logger.info(`⛔ BLOCKED: Channel mismatch. Received: ${channelString}, Active: ${this.activeChannel}`);
      return;
    }
    
    // Parse the message and extract bar + symbol
    const bar = this.parseSocketMessage(data, channelString);
    
    if (!bar) {
      Logger.warn('⚠️ parseSocketMessage returned null for channel:', channelString);
      return;
    }

    // Find all subscribers for this channel
    const matchingSubscribers = Object.values(this.subscribers).filter(
      subscriber => subscriber.channelString === channelString
    );

    if (matchingSubscribers.length === 0) {
      Logger.warn('⚠️ No subscribers found for channel:', channelString);
      return;
    }

    // Layer 2: Validate against subscriber's expected symbol
    const subscribedSymbol = matchingSubscribers[0]?.symbolInfo?.ticker;
    if (this.activeSymbol && subscribedSymbol !== this.activeSymbol) {
      Logger.warn(`⛔ BLOCKED: Subscriber symbol mismatch! Received channel: ${channelString}, Subscribed: ${subscribedSymbol}, Active: ${this.activeSymbol}`);
      return;
    }

    // Layer 3: Validate bar's embedded symbol matches expected symbol from channel
    if (bar.symbol) {
      const [baseCoin, cryptoCoin] = subscribedSymbol.split('/');
      const expectedSymbol = `${cryptoCoin}${baseCoin}`.toUpperCase();
      
      if (bar.symbol !== expectedSymbol) {
        Logger.warn(`⛔ BLOCKED: Bar symbol mismatch! Received: ${bar.symbol}, Expected: ${expectedSymbol} (from ${subscribedSymbol})`);
        return;
      }
      
      Logger.debug(`✅ ACCEPTED: Bar symbol ${bar.symbol} matches expected ${expectedSymbol}`);
    }

    Logger.debug(`🔔 Sending bar update to ${matchingSubscribers.length} subscriber(s) for ${channelString}`);
    
    matchingSubscribers.forEach(subscriber => {
      subscriber.onRealtimeCallback(bar);
    });
  }

  /**
   * Cleanup method
   */
  destroy() {
    if (this.socket) {
      if (typeof this.socket.disconnect === 'function') {
        this.socket.disconnect();
      } else if (typeof this.socket.close === 'function') {
        this.socket.close();
      }
      this.socket = null;
    }
    this.subscribers = {};
    this.activeSymbol = null;
    this.activeChannel = null;
  }
}

export default BaseDatafeed;
